import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, MenuItem, Popover, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import { C } from './tokens'
import RangePicker from './RangePicker'
import { rangeLabel } from './format'

export const PRESETS = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7', label: '7 days', days: 7 },
  { key: '30', label: '30 days', days: 30 }
]

export const COMPARE_OPTIONS = [
  { key: 'previous', label: 'vs previous period' },
  { key: 'year', label: 'vs same period last year' },
  { key: 'none', label: 'No comparison' }
]

export const CHANNEL_OPTIONS = [
  { key: 'ALL', label: 'All channels' },
  { key: 'ONLINE', label: 'Online' },
  { key: 'OLD_RMF', label: 'Legacy app' },
  { key: 'PARTNER', label: 'Partner (DoorDash, Uber Eats…)' },
  { key: 'INSTORE', label: 'In-store' }
]

const selectSx = {
  height: 36,
  fontSize: 14,
  bgcolor: '#fff',
  borderRadius: '8px',
  '.MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.borderStrong },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.brandText, borderWidth: 1.5 }
}

const segSx = {
  bgcolor: '#fff',
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 500,
    color: C.text,
    borderColor: C.border,
    px: 1.75,
    height: 36,
    '&.Mui-selected': { bgcolor: C.text, color: '#fff', '&:hover': { bgcolor: '#1F2937' } },
    '&.Mui-focusVisible': { outline: `2px solid ${C.brandText}`, outlineOffset: 2 }
  }
}

export default function FilterBar({ preset, from, to, today, compare, channel, onPreset, onRange, onCompare, onChannel, onExport, exportDisabled }) {
  const customRef = useRef(null)
  const sentinelRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [stuck, setStuck] = useState(false)

  /* A 1px marker sits just above the bar. Once it scrolls out of view the bar
     is pinned, and only then does it get its bottom border and shadow. The
     phone layout has a 56px app bar above the page, hence the offset. */
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return undefined
    const top = window.matchMedia('(min-width:600px)').matches ? 0 : 56
    const io = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), { rootMargin: `-${top + 1}px 0px 0px 0px`, threshold: 0 })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <>
    <Box ref={sentinelRef} aria-hidden sx={{ height: '1px', mb: '-1px' }} />
    <Box
      role="toolbar"
      aria-label="Dashboard filters"
      sx={{
        position: 'sticky',
        top: { xs: 56, sm: 0 },
        zIndex: 5,
        bgcolor: '#fff',
        borderBottom: `1px solid ${stuck ? C.border : 'transparent'}`,
        boxShadow: stuck ? '0 2px 8px rgba(17,24,39,0.06)' : 'none',
        transition: 'box-shadow 160ms ease, border-color 160ms ease',
        py: 1.5,
        mb: 3
      }}>
      <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 4 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={preset}
          aria-label="Date range"
          sx={segSx}
          onChange={(_, v) => {
            if (v === 'custom') setOpen(true)
            else if (v) onPreset(v)
          }}>
          {PRESETS.map(p => (
            <ToggleButton key={p.key} value={p.key} aria-label={p.label}>{p.label}</ToggleButton>
          ))}
          <ToggleButton
            value="custom"
            ref={customRef}
            aria-label="Custom date range"
            aria-haspopup="dialog"
            onClick={() => setOpen(true)}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: 16, mr: 0.75 }} />
            {preset === 'custom' ? rangeLabel(from, to) : 'Custom'}
          </ToggleButton>
        </ToggleButtonGroup>

        <Popover
          open={open}
          anchorEl={customRef.current}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { mt: 1, borderRadius: '12px', border: `1px solid ${C.border}`, boxShadow: '0 12px 32px rgba(17,24,39,0.12)' } }}>
          <RangePicker
            from={from}
            to={to}
            max={today}
            onChange={(a, b) => { setOpen(false); onRange(a, b) }}
          />
        </Popover>

        {/* Shown only to pages that use them — the Reports page uses neither. */}
        {onCompare && (
          <Select size="small" value={compare} onChange={e => onCompare(e.target.value)} sx={selectSx} inputProps={{ 'aria-label': 'Comparison' }}>
            {COMPARE_OPTIONS.map(o => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
          </Select>
        )}

        {onChannel && (
          <Select size="small" value={channel} onChange={e => onChannel(e.target.value)} sx={selectSx} inputProps={{ 'aria-label': 'Channel' }}>
            {CHANNEL_OPTIONS.map(o => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
          </Select>
        )}

        <Typography sx={{ fontSize: 13, color: C.textMuted, display: { xs: 'none', lg: 'block' } }}>
          {rangeLabel(from, to)}
        </Typography>

        <Button
          variant="outlined"
          onClick={onExport}
          disabled={exportDisabled}
          startIcon={<FileDownloadOutlinedIcon />}
          sx={{
            ml: 'auto',
            height: 36,
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 500,
            color: C.text,
            bgcolor: '#fff',
            borderColor: C.border,
            borderRadius: '8px',
            '&:hover': { borderColor: C.borderStrong, bgcolor: '#fff' },
            '&.Mui-focusVisible': { outline: `2px solid ${C.brandText}`, outlineOffset: 2 }
          }}>
          Export CSV
        </Button>
      </Box>
    </Box>
    </>
  )
}
